import express, {Application, Request, Response, NextFunction, RequestHandler} from 'express';
import statusMonitor from 'express-status-monitor';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { DatabaseConnection } from '@unidev-hub/database';
import {
  HealthService,
  HealthHistoryService,
  Status,
  MemoryHealthIndicator,
  DiskHealthIndicator,
  HttpHealthIndicator,
  DatabaseHealthIndicator,
  createHealthEndpoint,
  HealthResult,
  HealthIndicator
} from '@unidev-hub/health';
import { HealthOptions } from '../../types';

/**
 * Setup health checks for the application using @unidev-hub/health
 * Includes status monitoring UI and enhanced visualization
 *
 * @param app Express application
 * @param dbConnection Database connection (optional)
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Health check options
 */
export async function setupHealthChecks(
  app: Application,
  dbConnection: DatabaseConnection | null,
  config: ConfigManager,
  logger: Logger,
  options: HealthOptions = {}
): Promise<void> {
  try {
    // Check if health checks are enabled
    if (options.enabled === false) {
      logger.info('Health checks are disabled');
      return;
    }

    // Setup express-status-monitor if enabled
    const statusMonitorEnabled = options.statusMonitor?.enabled ??
      config.get<boolean>('health.statusMonitor.enabled', true);

    if (statusMonitorEnabled) {
      const statusMonitorOptions = {
        title: options.statusMonitor?.title ??
          config.get<string>('health.statusMonitor.title', 'Application Status Monitor'),
        theme: options.statusMonitor?.theme ??
          config.get<string>('health.statusMonitor.theme', 'default'),
        path: options.statusMonitor?.path ??
          config.get<string>('health.statusMonitor.path', '/status'),
        spans: [
          {
            interval: 1,     // Every second
            retention: 60    // Keep 60 datapoints (1 minute)
          },
          {
            interval: 5,     // Every 5 seconds
            retention: 60    // Keep 60 datapoints (5 minutes)
          },
          {
            interval: 15,    // Every 15 seconds
            retention: 60    // Keep 60 datapoints (15 minutes)
          }
        ],
        chartVisibility: {
          cpu: true,
          mem: true,
          load: true,
          responseTime: true,
          rps: true,
          statusCodes: true
        },
        healthChecks: []
      };

      // Apply status monitor middleware with safeguards
      try {
        app.use(statusMonitor(statusMonitorOptions));
        logger.info(`Status monitor UI available at ${statusMonitorOptions.path}`);
      } catch (monitorError) {
        logger.warn('Failed to initialize status monitor, falling back to basic health endpoints', monitorError as Error);
      }
    }

    // Get health check endpoint
    const healthEndpoint = options.endpoint || config.get<string>('health.endpoint', '/health');

    logger.debug(`Setting up health checks at ${healthEndpoint}`);

    // Create health service
    const healthService = new HealthService();

    // Set cache time (default is 10 seconds)
    const cacheTime = options.cacheTime || config.get<number>('health.cacheTime', 10000);
    healthService.setCacheTime(cacheTime);

    // Add memory health indicator
    const memoryThreshold = options.memoryThreshold || config.get<number>('health.memory.threshold', 85);
    healthService.addIndicator(new MemoryHealthIndicator({
      thresholdPercent: memoryThreshold
    }));

    // Add disk health indicator if enabled
    if (config.get<boolean>('health.disk.enabled', true)) {
      const diskPath = config.get<string>('health.disk.path', '/');
      const diskThreshold = config.get<number>('health.disk.threshold', 85);

      healthService.addIndicator(new DiskHealthIndicator({
        path: diskPath,
        thresholdPercent: diskThreshold
      }));
    }

    // Add database health indicator if a connection is provided
    if (dbConnection) {
      // Using type assertion to access the query method on dbConnection
      const dbConn = dbConnection as any;

      // Create a compatible database connection for health checks
      const dbHealthConnection = {
        config: {
          database: config.get<string>('database.database', 'unknown'),
          host: config.get<string>('database.host', 'unknown')
        },
        query: async (sql: string) => {
          // Ensure we have a query method or fallback gracefully
          if (typeof dbConn.query === 'function') {
            return await dbConn.query(sql, []);
          }
          logger.warn('Database connection does not have a query method');
          throw new Error('Database query method not available');
        }
      };

      // Create and add the database health indicator
      healthService.addIndicator(new DatabaseHealthIndicator({
        name: 'database',
        connection: dbHealthConnection,
        validationQuery: 'SELECT 1',
        timeout: config.get<number>('health.database.timeout', 5000)
      }));
    }

    // Add HTTP dependency health checks from options
    if (options.httpDependencies && options.httpDependencies.length > 0) {
      for (const dependency of options.httpDependencies) {
        healthService.addIndicator(new HttpHealthIndicator({
          name: dependency.name,
          url: dependency.url,
          method: dependency.method || 'GET',
          timeout: dependency.timeout || 5000,
          expectedStatus: dependency.expectedStatus || 200,
          headers: dependency.headers || {}
        }));

        logger.debug(`Added HTTP dependency health check for ${dependency.name}: ${dependency.url}`);
      }
    }

    // Add HTTP dependency health checks from config
    const configHttpDependencies = config.get<Array<Record<string, any>>>('health.dependencies.http', []);
    for (const dependency of configHttpDependencies) {
      healthService.addIndicator(new HttpHealthIndicator({
        name: dependency.name,
        url: dependency.url,
        method: dependency.method || 'GET',
        timeout: dependency.timeout || 5000,
        expectedStatus: dependency.expectedStatus || 200,
        headers: dependency.headers || {}
      }));

      logger.debug(`Added HTTP dependency health check for ${dependency.name}: ${dependency.url}`);
    }

    // Add custom health indicators if provided
    if (options.customIndicators && options.customIndicators.length > 0) {
      for (const indicator of options.customIndicators) {
        // Create a properly typed health indicator that returns the correct Status enum
        const healthIndicator: HealthIndicator = {
          name: indicator.name,
          check: async (): Promise<HealthResult> => {
            try {
              const result = await indicator.check();

              // Convert string status to Status enum if needed
              let statusValue: Status;
              if (typeof result.status === 'string') {
                // Map string status to Status enum
                switch (result.status.toUpperCase()) {
                  case 'UP':
                    statusValue = Status.UP;
                    break;
                  case 'DOWN':
                    statusValue = Status.DOWN;
                    break;
                  case 'DEGRADED':
                    statusValue = Status.DEGRADED;
                    break;
                  default:
                    statusValue = Status.UNKNOWN;
                }
              } else {
                // Already a Status enum value
                statusValue = result.status;
              }

              return {
                status: statusValue,
                details: result.details || {}
              };
            } catch (error) {
              logger.error(`Error in custom health indicator ${indicator.name}`, error as Error);
              return {
                status: Status.DOWN,
                details: { error: (error as Error).message }
              };
            }
          }
        };

        healthService.addIndicator(healthIndicator);
        logger.debug(`Added custom health indicator: ${indicator.name}`);
      }
    }

    // Create history service for tracking health metrics over time if enabled
    const historyEnabled = options.history?.enabled ??
      config.get<boolean>('health.history.enabled', true);
    let historyService: HealthHistoryService | undefined;

    if (historyEnabled) {
      const historyMaxEntries = options.history?.maxEntries ??
        config.get<number>('health.history.maxEntries', 100);
      const historyInterval = options.history?.interval ??
        config.get<number>('health.history.interval', 60000);

      historyService = new HealthHistoryService(healthService, {
        maxEntries: historyMaxEntries,
        collectionIntervalMs: historyInterval,
        autoStart: true
      });

      logger.debug('Health history service initialized', {
        maxEntries: historyMaxEntries,
        interval: `${historyInterval}ms`
      });
    }

    // Configure health endpoints with dashboard
    const dashboardEnabled = options.dashboard?.enabled ??
      config.get<boolean>('health.dashboard.enabled', true);
    const dashboardPath = options.dashboard?.path ??
      config.get<string>('health.dashboard.path', `${healthEndpoint}/dashboard`);
    const historyPath = config.get<string>('health.history.path', `${healthEndpoint}/history`);
    const historyLimit = options.history?.limit ??
      config.get<number>('health.history.limit', 50);

    const healthEndpointOptions = {
      path: healthEndpoint,
      livenessPath: `${healthEndpoint}/liveness`,
      readinessPath: `${healthEndpoint}/readiness`,
      detailedPath: `${healthEndpoint}/details`,
      enableDashboard: dashboardEnabled,
      dashboardPath: dashboardPath,
      historyPath: historyPath,
      historyLimit: historyLimit
    };

    // Create the health middleware
    const healthMiddleware = createHealthEndpoint(healthService, historyService, healthEndpointOptions);

    // Register the health middleware
    app.use('/', healthMiddleware as unknown as express.RequestHandler);


    logger.info(`Health checks configured at ${healthEndpoint}`);
    logger.info(`Health endpoints available at:`);
    logger.info(`- ${healthEndpoint} (Basic health check)`);
    logger.info(`- ${healthEndpoint}/liveness (Liveness probe)`);
    logger.info(`- ${healthEndpoint}/readiness (Readiness probe)`);
    logger.info(`- ${healthEndpoint}/details (Detailed health report)`);

    if (historyEnabled) {
      logger.info(`- ${historyPath} (Health metrics history)`);
    }

    if (dashboardEnabled) {
      logger.info(`- ${dashboardPath} (Interactive health dashboard)`);
    }

    if (statusMonitorEnabled) {
      const statusPath = options.statusMonitor?.path ??
        config.get<string>('health.statusMonitor.path', '/status');
      logger.info(`- ${statusPath} (Status monitor dashboard)`);
    }

    // Store health service in app locals for programmatic access
    app.locals.healthService = healthService;

    if (historyService) {
      app.locals.healthHistoryService = historyService;
    }

  } catch (error) {
    logger.error('Failed to setup health checks', error as Error);
    throw error;
  }
}

// Re-export health indicators and utilities from @unidev-hub/health
export {
  Status,
  HealthService,
  HealthHistoryService,
  MemoryHealthIndicator,
  DiskHealthIndicator,
  HttpHealthIndicator,
  DatabaseHealthIndicator,
  createHealthEndpoint
} from '@unidev-hub/health';

// Re-export types using 'export type' syntax for isolatedModules compatibility
export type {
  HealthResult,
  HealthIndicator
} from '@unidev-hub/health';
